import { type InsightStatusChangesModel } from "@doitintl/cmp-models";

import { type RequestConfig } from "../../Pages/CloudAnalytics/generateReport/types";
import { type CommonEventProperties } from "../../utils/segment";
import { type SegmentEventProperties } from "../../utils/useSegmentTrackEvent";
import { type CloudflowDTO } from "../Cloudflow/types";

export enum DisplayStatus {
  OPTIMIZED = "optimized",
  ACTIONABLE = "actionable",
  DISMISSED = "dismissed",
  PERMISSIONS_NEEDED = "permissions needed",
}

export type CloudProviderTag = "aws" | "gcp" | "azure" | "snowflake";

export type CloudProviderName = "Amazon Web Services" | "Google Cloud" | "Azure" | "Snowflake";

export type AccessType = "full" | "partial" | "no_access";

export enum InsightProviderId {
  TRUSTED_ADVISOR = "aws-trusted-advisor",
  COST_OPTIMIZATION_HUB = "aws-cost-optimization-hub",
  SECURITY_HUB = "aws-security-hub",
  GCP_RECOMMENDER = "gcp-recommender",
  FSK8S = "fsk8s",
  CORE = "core",
  CUSTOM = "custom",
}

export type InsightProviderWithPermissions =
  | InsightProviderId.TRUSTED_ADVISOR
  | InsightProviderId.COST_OPTIMIZATION_HUB
  | InsightProviderId.SECURITY_HUB
  | InsightProviderId.GCP_RECOMMENDER
  | InsightProviderId.FSK8S;

export enum InsightPriority {
  High = "High",
  Medium = "Medium",
  Low = "Low",
}

export enum InsightAccessType {
  Full = "full",
  Partial = "partial",
  No_access = "no_access",
}

export enum Category {
  FinOps = "FinOps",
  OperationalExcellence = "Operational excellence",
  PerformanceEfficiency = "Performance efficiency",
  Reliability = "Reliability",
  Security = "Security",
  Sustainability = "Sustainability",
}

export enum InsightsSortOrder {
  SAVINGS_ASCENDING = "savings_ascending",
  SAVINGS_DESCENDING = "savings_descending",
  PRIORITY_ASCENDING = "priority_ascending",
  PRIORITY_DESCENDING = "priority_descending",
}

export type RisksBreakdownValue = {
  critical: number;
  high: number;
  medium: number;
  low: number;
};

export type RisksBreakdownData = {
  value: RisksBreakdownValue;
  baseValue?: RisksBreakdownValue;
  dimensionValues: string[];
};

type RisksBreakdown = {
  dimensions: string[];
  data: RisksBreakdownData[];
};

export type Risks = {
  isOptimized: boolean;
  breakdown?: RisksBreakdown;
};

export type RiskTypeKey =
  | "operationalExcellenceRisks"
  | "performanceRisks"
  | "reliabilityRisks"
  | "securityRisks"
  | "sustainabilityRisks";

export const RiskTypes: {
  key: RiskTypeKey;
  label: string;
}[] = [
  { key: "operationalExcellenceRisks", label: "Operational excellence risks" },
  { key: "performanceRisks", label: "Performance risks" },
  { key: "reliabilityRisks", label: "Reliability risks" },
  { key: "securityRisks", label: "Security risks" },
  { key: "sustainabilityRisks", label: "Sustainability risks" },
];

export type BreakdownData = {
  value: number;
  baseValue: number;
  dimensionValues: string[];
};

export type Breakdown = {
  dimensions: string[];
  data: BreakdownData[];
};

export type PotentialDailySavings = {
  isOptimized: boolean;
  breakdown: Breakdown | null;
};

export type CustomProblems = {
  isOptimized: boolean;
  unitShortSingular: string;
  unitShortPlural: string;
  unitLongSingular: string;
  unitLongPlural: string;
  breakdown: Breakdown | null;
};

export type Results = {
  isRelevant: boolean;
  reportConfig: RequestConfig | null;

  potentialDailySavings: PotentialDailySavings | null;
  customProblems: CustomProblems | null;

  operationalExcellenceRisks: Risks | null;
  performanceRisks: Risks | null;
  reliabilityRisks: Risks | null;
  securityRisks: Risks | null;
  sustainabilityRisks: Risks | null;
};

export type CustomInsightAttributes = {
  published: string;
  publishingUserEmail: string;
  showPublishingUser: boolean;
};

export type InsightPublishingUser = {
  displayName: string;
  avatarUrl: string;
  title: string;
  onVacation: boolean;
};

export type LastStatusChange = {
  userId: string;
  lastChangedAt: string;
};

export type ServerInsightResult = Omit<Results, "securityRisks"> & { securityRisks: Risks | null };

export type ServerInsight = Omit<Insight, "results"> & { results: ServerInsightResult | null };

export type InsightPermissions = {
  accessType: AccessType;
};

export type ExpertNote = {
  note: string;
  createdAt: string;
};

type CloudFlowParam = {
  context: Record<string, string>;
  results: Array<Record<string, string[]>>;
};

export type Insight = {
  providerId: string;
  key: string;
  canBeIndividuallyExecuted: boolean;
  customerId: string;
  displayStatus: DisplayStatus;
  lastStatusChange: LastStatusChange | null;
  lastUpdated: string;
  title: string;
  shortDescription: string;
  detailedDescriptionMdx: string;
  expertNote: ExpertNote | null;
  cloudFlowBlueprintId: string; // Represents the pre-defined cloudflow template for the insight
  cloudFlowParams: CloudFlowParam[];
  cloudTags: CloudProviderTag[];
  tags: string[];
  categories: Category[];
  isInternal: boolean;
  supportCategory: string;
  results: Results | null;
  reportUrl: string;
  customInsightAttributes: CustomInsightAttributes | null;
  userStatusChanges: InsightStatusChangesModel | null;
  easyWinDescription: string;
  publishingUser: InsightPublishingUser | null;
  permissions: InsightPermissions | null;
  priority: InsightPriority | null;
};

export type SavingsPeriod = "daily" | "monthly" | "yearly";

type NonEntitledInsightsResult = {
  // Number of insights that are relevant to a customer (but may not have any value and be already optimized)
  numberOfRelevantInsights: number;

  // Number of insights that are relevant but not yet optimized
  numberOfActionableInsights: number;

  // Total value (in USD in the case of potentialDailySavings or simple integer count for customProblems)
  value: number;
};

export type NonEntitledInsightsResults = {
  potentialDailySavings: NonEntitledInsightsResult;
  customProblems: NonEntitledInsightsResult;
};

type NoAccessPotentialDailySavings = {
  isOptimized: boolean;
  value: number;
};

export type NoAccessInsight = {
  providerId: string;
  key: string;
  potentialDailySavings?: NoAccessPotentialDailySavings;
  customerId: string;
  title: string;
  shortDescription: string;
  supportCategory: string;
  cloudTags: string[];
  categories: string[];
  isRelevant: boolean;
  easyWinDescription: string;
};

export type InsightResultsResponse<T = Insight> = {
  noAccessResults: NoAccessInsight[];
  results: T[];
  nonEntitledResultsSummary: NonEntitledInsightsResults;
};

export type InsightMissingPermissionsResponse = {
  cloud: CloudProviderTag;
  providerId: InsightProviderId;
  scopes: string[];
}[];

export type InsightCloudFlowsResponse = {
  id: string;
}[];

// Response type correction
export type CloudFlowDetailsResponse = Omit<CloudflowDTO, "nodes" | "createdAt" | "updatedAt | nextRun"> & {
  createdAt: string;
  updatedAt: string;
  nextRun?: string | null;
};

export type InsightCloudFlows = Pick<CloudFlowDetailsResponse, "id" | "name" | "status" | "createdAt">;

export interface InsightsListEventProperties extends CommonEventProperties, SegmentEventProperties {
  permissionsNeededInsights: number;
  selectedInsightsCount: number;
}

export interface InsightsListViewedProperties extends InsightsListEventProperties {
  insightsSortOrder: string;
  insightsSavingsPeriod: string;
  insightsFiltersSelected: string[];
  insightsSearchTerms: string;
  customInsightsCount: number;
  insightsTotalSavings: string;
  insightsHighestSavings: number;
  cloudflowInsightsWithSavingsCount: number;
}

export interface InsightsAddPermissionsProperties extends InsightsListEventProperties {
  accountsMissingPermissions: string[];
  insightsSavingsPeriod: string;
  customInsightsCount: number;
  insightsTotalSavings: string;
  insightsHighestSavings: number;
}

export type AddPermissionsEventType =
  | "addTAPermissionClickedEvent"
  | "addCostOptPermissionClickedEvent"
  | "addSecurityHubPermissionClickedEvent"
  | "addGCPRecommenderPermissionClickedEvent"
  | "addGKEPermissionClickedEvent";

export interface InsightProperties extends CommonEventProperties, SegmentEventProperties {
  insightKey: string;
  insightTitle: string;
  insightProvider: string;
  isOptimized: boolean;
  isRelevant: boolean;
  insightCloudTags: string[];
  insightCategories: string[];
  insightTags?: string[];
  insightPriority?: string;
  insightHasCloudflowBlueprint: boolean;
  isCustomInsight: boolean;
  insightLastUpdated: string;
  insightDailySavings?: number;
  insightUserStatus?: string;
  insightSupportCategory?: string;
  insightSavingsPeriod?: string;
  insightTotalSavings?: string;
  customProblemsTotal?: number;
  customProblemsUnit?: string;
}

export interface CustomInsightProperties extends InsightProperties {
  insightPublished: string;
  insightPublishingUser: string;
}

export interface InsightsUpsellPageViewedProperties extends CommonEventProperties, SegmentEventProperties {
  totalYearlySavings: string;
  dciTotalCost: number;
  monthlyCloudSpend: number;
  insightsTotalSavings: number;
  insightsHighestSavings: number;
  actionableInsights: number;
  optimizedInsights: number;
  irrelevantInsights: number;
  hasCustomInsights: boolean;
}

export type StatusDetails = {
  date: string;
  userDisplayName: string;
};

export type Filters = {
  providers: CloudProviderTag[];
  categories: Category[];
  status: DisplayStatus[];
  priority: InsightPriority[];
  tags: string[];
  additionalOptions: string[];
};

export type SecurityRisksInput = {
  critical: number;
  high: number;
  medium: number;
  low: number;
};
